import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  RadioGroup,
  FormControlLabel,
  Radio,
  Select,
  MenuItem,
} from '@mui/material';

// 「doc」 にすべての動的フィールドが入る想定
interface DoctorRecord {
  id: number;
  doc: Record<string, any>;   // 動的なフィールドをすべて格納
  login_id?: string;
  login_pass?: string;
  created_at?: string;
  updated_at?: string;
}

const DoctorList: React.FC = () => {
  const [doctors, setDoctors] = useState<DoctorRecord[]>([]);
  const [selectedDoctor, setSelectedDoctor] = useState<DoctorRecord | null>(null);

  // dialogType: "detail" | "edit" | null
  const [dialogType, setDialogType] = useState<"detail" | "edit" | null>(null);
  // 削除確認用ダイアログ
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  // 削除対象
  const [doctorToDelete, setDoctorToDelete] = useState<DoctorRecord | null>(null);

  // 編集用フィールド（動的なdocをコピー）
  const [editedDoc, setEditedDoc] = useState<Record<string, any>>({});

  // データ取得
  const fetchData = async () => {
    const response = await fetch('https://xs525443.xsrv.jp/kansaibou/doctor_list_api.php');
    const data = await response.json();
    console.log("Fetched data:", data);
    setDoctors(data);
  };

  useEffect(() => {
    fetchData();
  }, []);

  // 詳細ダイアログ
  const handleDetail = (doctor: DoctorRecord) => {
    setSelectedDoctor(doctor);
    setDialogType("detail");
  };

  // 編集ダイアログを開く
  const handleEdit = (doctor: DoctorRecord) => {
    setSelectedDoctor(doctor);
    // docをコピーしてeditedDocにセット
    setEditedDoc({ ...doctor.doc });
    setDialogType("edit");
  };

  // ダイアログを閉じる
  const handleClose = () => {
    setSelectedDoctor(null);
    setDialogType(null);
    setEditedDoc({});
  };

  // 削除確認ポップアップを表示
  const handleDeleteConfirm = (doctor: DoctorRecord) => {
    setDoctorToDelete(doctor);
    setDeleteConfirmOpen(true);
  };

  // 削除実行
  const handleDeleteYes = async () => {
    if (!doctorToDelete) return;
    const doctorId = doctorToDelete.id;

    // 実際の削除リクエスト
    await fetch('https://xs525443.xsrv.jp/kansaibou/doctor_delete_api.php', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ id: doctorId }),
    });

    // ローカルのデータを更新
    setDoctors((prev) => prev.filter((d) => d.id !== doctorId));

    setDeleteConfirmOpen(false);
    setDoctorToDelete(null);
    alert('The record has been deleted.');
  };

  // 削除キャンセル
  const handleDeleteNo = () => {
    setDeleteConfirmOpen(false);
    setDoctorToDelete(null);
  };

  // ****************** 動的フィールドを編集 ****************** //
  // 文字列フィールドのonChange
  const handleChange = (key: string) => (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setEditedDoc((prev) => ({ ...prev, [key]: e.target.value }));
  };

  // ラジオやセレクトの場合
  const handleSelectChange = (key: string) => (e: React.ChangeEvent<HTMLInputElement>) => {
    setEditedDoc((prev) => ({ ...prev, [key]: e.target.value }));
  };

  // ファイル変更
  const handleFileChange = (key: string) => (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      setEditedDoc((prev) => ({ ...prev, [key]: e.target.files?.[0] }));
    }
  };

  // ****************** 保存処理 ****************** //
  const handleSave = async () => {
    if (!selectedDoctor) return;

    // フォームデータを作成
    const formData = new FormData();
    formData.append('id', String(selectedDoctor.id));

    // editedDoc内を走査
    for (const [key, value] of Object.entries(editedDoc)) {
      if (
        (key === 'photoProfile' || key === 'photoLicense') &&
        value instanceof File
      ) {
        // ファイルはappend
        formData.append(key, value);
      } else {
        // 文字列フィールド
        formData.append(key, String(value));
      }
    }

    // Update API呼び出し
    await fetch('https://xs525443.xsrv.jp/kansaibou/doctor_update_api.php', {
      method: 'POST',
      body: formData,
    });

    alert('The changes have been saved.');

    fetchData(); // 更新後のデータ再取得
    handleClose();
  };

  return (
    <Container maxWidth="lg" sx={{ mt: 4 }}>
      <Typography variant="h4" component="h1" gutterBottom>
        Doctor List
      </Typography>
      {/* Doctor Table */}
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>ID</TableCell>
              <TableCell>Name</TableCell>
              <TableCell>Mobile</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {doctors.map((doctor) => {
              const { doc } = doctor;
              const fullName = [
                doc.firstName,
                doc.middleName,
                doc.lastName,
              ].filter(Boolean).join(" ") || "N/A";

              return (
                <TableRow key={doctor.id}>
                  <TableCell>{doctor.id}</TableCell>
                  <TableCell>{fullName}</TableCell>
                  <TableCell>{doc.mobile || "N/A"}</TableCell>
                  <TableCell>
                    <Button
                      variant="contained"
                      color="primary"
                      sx={{ mr: 1 }}
                      onClick={() => handleDetail(doctor)}
                    >
                      View
                    </Button>
                    <Button
                      variant="contained"
                      color="primary"
                      sx={{ mr: 1 }}
                      onClick={() => handleEdit(doctor)}
                    >
                      Edit
                    </Button>
                    <Button
                      variant="contained"
                      color="secondary"
                      onClick={() => handleDeleteConfirm(doctor)}
                    >
                      Delete
                    </Button>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>

      {/* ------ 詳細ダイアログ ------ */}
      <Dialog open={dialogType === "detail"} onClose={handleClose} maxWidth="sm" fullWidth>
        <DialogTitle>Doctor Details</DialogTitle>
        <DialogContent dividers>
          {selectedDoctor && (
            <>
              <Typography variant="subtitle1">ID: {selectedDoctor.id}</Typography>
              <Typography variant="body2" sx={{ mb: 2 }}>
                Created: {selectedDoctor.created_at} / Updated: {selectedDoctor.updated_at}
              </Typography>
              {/* doc内を動的に列挙 */}
              {Object.entries(selectedDoctor.doc).map(([key, value]) => {
                if (key === "photoProfile" && typeof value === "string" && value) {
                  return (
                    <div key={key} style={{ marginTop: "1rem" }}>
                      <Typography variant="subtitle2">{key}:</Typography>
                      <img
                        src={`https://xs525443.xsrv.jp/kansaibou/up/${value}?_=${Date.now()}`}
                        alt="Profile"
                        style={{ width: "100%", maxWidth: "300px", marginTop: "10px" }}
                      />
                    </div>
                  );
                }
                if (key === "photoLicense" && typeof value === "string" && value) {
                  return (
                    <div key={key} style={{ marginTop: "1rem" }}>
                      <Typography variant="subtitle2">{key}:</Typography>
                      <img
                        src={`https://xs525443.xsrv.jp/kansaibou/up/${value}?_=${Date.now()}`}
                        alt="License"
                        style={{ width: "100%", maxWidth: "300px", marginTop: "10px" }}
                      />
                    </div>
                  );
                }
                return (
                  <div key={key} style={{ marginTop: "0.5rem" }}>
                    <Typography variant="subtitle2">{key}:</Typography>
                    <Typography variant="body1">{String(value)}</Typography>
                  </div>
                );
              })}
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose} color="primary">
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* ------ 編集ダイアログ (動的フィールド) ------ */}
      <Dialog open={dialogType === "edit"} onClose={handleClose} maxWidth="sm" fullWidth>
        <DialogTitle>Edit Doctor</DialogTitle>
        <DialogContent dividers>
          {Object.entries(editedDoc).map(([key, value]) => {
            // 写真フィールド（ファイル）
            if (key === "photoProfile" || key === "photoLicense") {
              return (
                <div key={key} style={{ marginTop: "1rem" }}>
                  <Typography variant="subtitle2">{key}</Typography>
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleFileChange(key)}
                  />
                  {/* 既存画像表示 (valueが文字列なら) */}
                  {typeof value === "string" && value && (
                    <img
                      src={`https://xs525443.xsrv.jp/kansaibou/up/${value}?_=${Date.now()}`}
                      alt={key}
                      style={{ width: "100%", maxWidth: "300px", marginTop: "10px" }}
                    />
                  )}
                </div>
              );
            }

            // 性別フィールド（例: radio）
            if (key === "sex") {
              return (
                <div key={key} style={{ marginTop: "1rem" }}>
                  <Typography variant="subtitle2">Sex</Typography>
                  <RadioGroup
                    row
                    value={value || ""}
                    onChange={(e) =>
                      setEditedDoc((prev) => ({ ...prev, [key]: e.target.value }))
                    }
                  >
                    <FormControlLabel value="Male" control={<Radio />} label="Male" />
                    <FormControlLabel value="Female" control={<Radio />} label="Female" />
                  </RadioGroup>
                </div>
              );
            }

            // 生年月日を year/month/day で持っていればSelectなどにする例
            if (key === "birthYear" || key === "birthMonth" || key === "birthDay") {
              return (
                <TextField
                  key={key}
                  label={key}
                  fullWidth
                  margin="normal"
                  value={String(value)}
                  onChange={(e) =>
                    setEditedDoc((prev) => ({ ...prev, [key]: e.target.value }))
                  }
                />
              );
            }

            // 通常の文字列フィールド
            return (
              <TextField
                key={key}
                label={key}
                fullWidth
                margin="normal"
                multiline={typeof value === "string" && value.length > 40} 
                value={String(value)}
                onChange={(e) =>
                  setEditedDoc((prev) => ({ ...prev, [key]: e.target.value }))
                }
              />
            );
          })}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose} color="secondary">
            Cancel
          </Button>
          <Button onClick={handleSave} color="primary">
            Save
          </Button>
        </DialogActions>
      </Dialog>

      {/* ------ Delete Confirm Dialog ------ */}
      <Dialog open={deleteConfirmOpen} onClose={handleDeleteNo}>
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent dividers>
          Are you sure you want to delete this record?
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDeleteNo} color="primary">
            No
          </Button>
          <Button onClick={handleDeleteYes} color="secondary">
            Yes
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default DoctorList;
